/**
 * Basket JS controller.
 *
 * This class handles the order basket logic.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

/**
 * Class Basket. This gets mapped with the keyword Basket and its
 * docready method is fired automatically on pages where there's a
 * class called 'Basket' on the html tag.
 */
class Basket {

    /**
     * The class constructor is for binding class properties.
     */
    constructor() {
    }

    /**
     * Cache dom elements for use in the methods
     */
    cache() {

        this.noProductsHeading          = $( '#js-no-products-heading' );
        this.noProductsSubtext          = $( '#js-no-products-subtext' );
        this.basketHeader               = $( '#js-basket-header' );
        this.basketHeading              = $( '#js-basket-heading' );
        this.basketProducts             = $( '#js-basket-products' );
        this.orderFormContainer         = $( '#js-order-form-container' );
        this.orderSection               = $( '#js-order-section' );
        this.productsInBasketIndicators = $( '.js-checkout-link-number' );
        this.emptyBasketButton          = $( '#js-empty-basket' );

        this.strings = {
            add: themeData.themeStrings.orderform.add,
            reduce: themeData.themeStrings.orderform.reduce,
            amount: themeData.themeStrings.orderform.amount,
            name: themeData.themeStrings.orderform.name
        };
    }

    /**
     * This handles all event binding of the class.
     */
    events() {

        this.emptyBasketButton.on( 'click', () => {
            this.emptyBasket();
        });

        window.wplf.successCallbacks.push( res => {

            if ( res.clear_basket === 1 ) {
                this.emptyBasket( 1 );
            }
        });

    }

    /**
     * This fetches the order basket from localStorage and parses it
     * for later use. If there's no orders in the basket, this returns an empty object.
     *
     */
    getOrderBasket() {

        let orderBasket = localStorage.getItem( 'orderBasket' );

        if ( ! Theme.Common.empty( orderBasket ) ) {

            orderBasket = JSON.parse( orderBasket );

        } else {

            orderBasket = {};
        }

        return orderBasket;
    }

    /**
     * This adds a product to the order basket in localStorage. After that
     * it calls the method that updates the product list.
     *
     * @param {object} product The product to be added.
     */
    addToOrderBasket( product ) {

        const orderBasket = this.getOrderBasket();

        if ( orderBasket.hasOwnProperty( product.id ) ) {
            orderBasket[ product.id ].count++;
        } else {
            orderBasket[ product.id ] = { name: product.name, count: 1 };
        }

        localStorage.setItem( 'orderBasket', JSON.stringify( orderBasket ) );

        this.listProducts();
    }


    /**
     * This reduces the count of a product in the order basket. After that
     * it calls the method that updates the product list.
     *
     * @param {object} product The product to be reduced.
     */
    reduceFromOrderBasket( product ) {

        const orderBasket = this.getOrderBasket();

        if ( orderBasket.hasOwnProperty( product.id ) ) {

            if ( orderBasket[ product.id ].count > 0 ) {
                orderBasket[ product.id ].count--;
            } else {
                orderBasket[ product.id ].count = 0;
            }
        }

        localStorage.setItem( 'orderBasket', JSON.stringify( orderBasket ) );

        this.listProducts();
    }

    /**
     * This controls the count of a product in the order basket. After that
     * it calls the method that updates the product list.
     *
     * @param {object} product The product to be changed.
     * @param {int} amount The number of amount after change
     */
    changeOrderBasketAmount( product, newValue ) {

        const orderBasket = this.getOrderBasket();

        if ( orderBasket.hasOwnProperty( product.id ) ) {
            orderBasket[ product.id ].count = newValue;
        }

        localStorage.setItem( 'orderBasket', JSON.stringify( orderBasket ) );

        this.listProducts();
    }

    /**
     * This lists all products and sets their counters accordingly.
     * This also concatenates a text that is added to the sent order form textarea.
     *
     * If there are no products in the basket, this shows corresponding information to the user.
     */
    listProducts() {

        this.basketProducts.empty();

        const orderBasket = this.getOrderBasket();
        let productData   = '';
        let itemsInBasket = 0;

        if ( ! Theme.Common.empty( orderBasket ) ) {

            _.forEach( orderBasket, ( property, id ) => {

                if ( property.count > 0 ) {

                    const listItem     = $( '<li class="js-basket-product basket__product"></li>' );
                    const itemName     = $( '<p class="h6 product__heading">' + property.name + '</p>' );
                    const itemControls = $( '<div class="js-product-controls product__controls" data-id="' + id + '" data-name="' + property.name + '"></div>' );

                    const itemReduce   = $( '<button class="js-reduce-count product__reduce"><i class="icon ion-ios-remove-circle" aria-hidden="true"></i><span class="u-sr-only">' + this.strings.reduce + '</span></div>' );
                    const itemAdd      = $( '<button class="js-add-count product__add"><i class="icon ion-ios-add-circle" aria-hidden="true"></i><span class="u-sr-only">' + this.strings.add + '</span></div>' );
                    const itemCount    = $( '<input type="number" class="product__amount js-product-amount" value="' + property.count + '"><span class="u-sr-only">' + this.strings.amount + ': </span></input>' );

                    itemControls.append( itemReduce );
                    itemControls.append( itemCount );
                    itemControls.append( itemAdd );

                    listItem.append( itemName );
                    listItem.append( itemControls );

                    this.basketProducts.append( listItem );

                    productData += this.strings.name + ': ' + property.name + ' - ' + this.strings.amount + ': ' + property.count + '\n';

                    itemsInBasket = 1;
                }
            });

            if ( itemsInBasket ) {

                // Hide default 'no products' elements.
                this.noProductsHeading.addClass( 'js-hidden' );
                this.noProductsSubtext.addClass( 'js-hidden' );

                // Show all necessary product info elements.
                this.basketHeader.removeClass( 'js-hidden' );
                this.basketProducts.removeClass( 'js-hidden' );
                this.orderFormContainer.removeClass( 'js-hidden' );
                this.emptyBasketButton.removeClass( 'js-hidden' );

                this.bindBasketControls();

            } else {

                // Show defaults again & focus on title for screenreaders.
                this.noProductsHeading.removeClass( 'js-hidden' ).focus();
                this.noProductsSubtext.removeClass( 'js-hidden' );

                // Hide elements related to products to be ordered.
                this.emptyBasketButton.addClass( 'js-hidden' );
                this.basketHeader.addClass( 'js-hidden' );
                this.basketProducts.addClass( 'js-hidden' );
                this.orderFormContainer.addClass( 'js-hidden' );
            }

            // Add selected products to the sent form and update
            // The number icon of total product amount.
            this.addProductsToForm( productData );
            this.updateBasketIndicators();
        }
    }

    /**
     * This binds all controls after each update of the list.
     */
    bindBasketControls() {

        // Loop through each of the add to basket buttons and bind a click handler to each of them.
        $( '.js-add-count' ).each( ( index, button ) => {

            $( button ).on( 'click', () =>  {

                const $button = $( button );
                const $productControls = $button.closest( '.js-product-controls' );

                const product = {
                    id: $productControls.attr( 'data-id' ),
                    name: $productControls.attr( 'data-name' )
                };

                this.addToOrderBasket( product );

            });

        });

        // Loop through each of the reduce from basket buttons and bind a click handler to each of them.
        $( '.js-reduce-count' ).each( ( index, button ) => {

            $( button ).on( 'click', () =>  {

                const $button = $( button );
                const $productControls = $button.closest( '.js-product-controls' );

                const product = {
                    id: $productControls.attr( 'data-id' ),
                    name: $productControls.attr( 'data-name' )
                };

                this.reduceFromOrderBasket( product );

            });

        });

        // Loop through each input field to bind them to change the total value of items in the basked when their own value changes
        $( '.js-product-amount' ).each( ( index, amount ) => {
            $( amount ).on( 'change', () => {

                const $amount = $( amount );
                const newValue = parseInt( $amount.val() );
                const $productControls = $amount.closest( '.js-product-controls' );

                const product = {
                    id: $productControls.attr( 'data-id' ),
                    name: $productControls.attr( 'data-name' )
                };

                this.changeOrderBasketAmount( product, newValue );

            });
        });

    }

    /**
     * This add given product string to a hidden textarea element in the order form.
     */
    addProductsToForm( productData ) {

        const productField = this.orderFormContainer.find( 'form' ).find( '#products' );
        productField.empty();
        productField.append( productData );
    }

    /**
     * This empties the localStorage set of products and
     * either calls listProducts or hides everything else but the
     * order confirmation message.
     *
     * @param {boolean} ordered
     */
    emptyBasket( ordered = 0 ) {

        localStorage.removeItem( 'orderBasket' );

        if ( ordered === 1 ) {

            // Hide everything else but order confirmation message.
            this.noProductsHeading.addClass( 'js-hidden' );
            this.noProductsSubtext.addClass( 'js-hidden' );
            this.emptyBasketButton.addClass( 'js-hidden' );
            this.basketHeader.addClass( 'js-hidden' );
            this.basketProducts.addClass( 'js-hidden' );

            // Focus the order form container that now contains the order confirmation.
            this.orderFormContainer.addClass( 'js-order-complete' );
            this.orderSection.trigger( 'focus' );

        } else {
            this.listProducts();
        }
    }

    /**
     * This updates the indicator number and the screen reader equivalent by
     * fetching the basket data and adding together the amounts of each product.
     *
     */
    updateBasketIndicators() {

        const orderBasket = this.getOrderBasket();
        let amount        = 0;

        _.forEach( orderBasket, ( property ) => {
            amount += property.count;
        });

        this.productsInBasketIndicators.text( amount );
    }

    /**
     * Run when the document is ready.
     */
    docReady() {
        this.cache();
        this.events();
        this.listProducts();
        this.updateBasketIndicators();
    }
}

// Export the class reference.
module.exports = Basket;



// WEBPACK FOOTER //
// ./assets/scripts/basket.js